import { validateDisplayName } from '@/lib/display-name';

export const ONBOARDING_APPROVED_STATES = [
  'VA', 'MD', 'DC', 'NC', 'SC', 'PA', 'WV', 'DE', 'NJ', 'NY', 'FL', 'TX', 'CA', 'GA',
] as const;

export const ONBOARDING_VERIFY_STEP = 6;
export const ONBOARDING_TERMS_STEP = 5;

export const ONBOARDING_COMPLETE_STORAGE_KEY = 'bagtag_onboarding_complete';

export type OnboardingFormState = {
  name: string;
  handle: string;
  email: string;
  phone: string;
  ageConfirmed: boolean;
  state: string;
  handicap: string;
  ghin: string;
  venmo: string;
  avatar: string;
  tosAccepted: boolean;
  affiliationDisclaimerAcked: boolean;
};

export function createInitialOnboardingForm(avatar = 'golf:1'): OnboardingFormState {
  return {
    name: '',
    handle: '',
    email: '',
    phone: '',
    ageConfirmed: false,
    state: '',
    handicap: '',
    ghin: '',
    venmo: '',
    avatar,
    tosAccepted: false,
    affiliationDisclaimerAcked: false,
  };
}

/** Lowercase handle for storage; strips invalid chars (does not map dots to underscores). */
export function sanitizeOnboardingHandle(handle: string): string {
  return handle.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
}

/** True when all required onboarding fields are valid before accept-invitation. */
export function isOnboardingFormComplete(form: OnboardingFormState): boolean {
  const h = form.handle.trim();
  return (
    form.ageConfirmed
    && ONBOARDING_APPROVED_STATES.includes(form.state as (typeof ONBOARDING_APPROVED_STATES)[number])
    && form.name.trim().length >= 2
    && h.length >= 3
    && h.length <= 24
    && !validateDisplayName(form.name, form.handle)
    && form.email.trim().length > 0
    && form.handicap.trim().length > 0
    && form.tosAccepted
    && form.affiliationDisclaimerAcked
  );
}

/** Merge persisted snapshot with defaults (magic-link / new-tab resume). */
export function mergeSavedOnboardingForm(
  saved: Partial<OnboardingFormState> | null | undefined,
  initial: OnboardingFormState,
): OnboardingFormState {
  if (!saved) return { ...initial };
  return { ...initial, ...saved };
}

/** Prefer live in-memory form over stale localStorage when both exist. */
export function resolveSubmitOnboardingForm(
  live: OnboardingFormState,
  saved: Partial<OnboardingFormState> | null | undefined,
  initial: OnboardingFormState,
): OnboardingFormState {
  const merged = mergeSavedOnboardingForm(saved, initial);
  return { ...merged, ...live };
}

export type AcceptInvitationPayload = {
  token: string;
  auth_user_id: string;
  name: string;
  handle: string;
  email: string;
  phone?: string;
  handicap?: number;
  ghin_number?: string;
  venmo?: string;
  avatar: string;
  tos_accepted: boolean;
  affiliation_disclaimer_acked: boolean;
};

/** Build POST body for accept-invitation from onboarding form state. */
export function buildAcceptInvitationPayload(
  form: OnboardingFormState,
  token: string,
  authUserId: string,
): AcceptInvitationPayload {
  return {
    token,
    auth_user_id: authUserId,
    name: form.name.trim(),
    handle: sanitizeOnboardingHandle(form.handle),
    email: form.email.trim().toLowerCase(),
    phone: form.phone.trim() || undefined,
    handicap: form.handicap ? Number(form.handicap) : undefined,
    ghin_number: form.ghin.trim() || undefined,
    venmo: form.venmo.trim() || undefined,
    avatar: form.avatar,
    tos_accepted: form.tosAccepted,
    affiliation_disclaimer_acked: form.affiliationDisclaimerAcked,
  };
}

/** Mirror accept-invitation edge function display-name rules. */
export function validateAcceptInvitationName(name: string, handle: string): string | null {
  const trimmedName = String(name).trim();
  if (!/\s/.test(trimmedName)) {
    return 'Display name must be a full name (e.g. Joe Kimani)';
  }
  if (trimmedName.toLowerCase().replace(/\s+/g, '') === handle.toLowerCase()) {
    return 'Display name must be different from username';
  }
  return null;
}

/**
 * Detect identity derived from email local-part (production bug pattern).
 * e.g. mainas.nyumbani@gmail.com → name mainas.nyumbani, handle mainas_nyumbani
 */
export function isEmailDerivedIdentity(
  email: string,
  name: string,
  handle: string,
): boolean {
  const local = email.split('@')[0]?.toLowerCase() ?? '';
  if (!local) return false;

  const normalizedName = name.trim().toLowerCase();
  const normalizedHandle = handle.trim().toLowerCase();

  if (normalizedName === local) return true;

  const underscoreLocal = local.replace(/\./g, '_');
  if (normalizedHandle === underscoreLocal) return true;

  const strippedLocal = local.replace(/[^a-z0-9_]/g, '');
  if (normalizedHandle === strippedLocal && strippedLocal.length >= 3) return true;

  return false;
}

export function markOnboardingComplete(): void {
  try {
    localStorage.setItem(ONBOARDING_COMPLETE_STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function isOnboardingCompleteFlagSet(): boolean {
  try {
    return Boolean(localStorage.getItem(ONBOARDING_COMPLETE_STORAGE_KEY));
  } catch {
    return false;
  }
}

export function clearOnboardingCompleteFlag(): void {
  try {
    localStorage.removeItem(ONBOARDING_COMPLETE_STORAGE_KEY);
  } catch {
    // ignore
  }
}
